import { routes2 } from '@linqpal/models'
import React, { useCallback, useEffect, useState } from 'react'
import { View } from 'react-native'
import { Divider } from 'react-native-paper'
import BuilderBottomModal from '../../../../ui/molecules/BuilderBottomModal'
import { InputField } from '../../../../ui/atoms/builder-2.0/InputField'
import { PayTermsAndConditions } from '../../../Auth/TermsAndConditions'
import { Spacer } from '../../../../ui/atoms'
import { ChoosePaymentModal } from '../../../../ui/organisms/ChoosePaymentModal'
import Loading from '../../../Loading'
import { useTranslation } from 'react-i18next'
import { useResponsive } from '../../../../utils/hooks'
import { SuccessMessage } from './SuccessMessage'
import { DetailsRowItem } from './DetailsRowItem'
import { PaymentButton } from './PaymentButton'
import { FeeOrDiscount } from './FeeOrDiscount'
import { currencyMask } from '../../../../utils/helpers/masking'

export type PricingPackageType = {
  totalAmount?: string | number
  fee?: string | number
  discount?: number
}

type PayInvoiceModalProps = {
  modal: boolean
  setModal: (val: boolean) => void
  navigation?: any
  invoices: any[]
  account: any
  paymentPlan?: any
  showModal?: (val: boolean) => void
}

export const PayInvoiceModal = ({
  modal,
  setModal,
  navigation,
  invoices,
  account,
  paymentPlan,
  showModal,
}: PayInvoiceModalProps) => {
  const [loading, setLoading] = useState(false)
  const [pricingPackage, setPricingPackage] =
    useState<PricingPackageType | null>(null)
  const invoice = invoices[0]
  const builderCreatedInvoice = invoice?.company_id === ''
  const [paymentMethod, setMethod] = useState(account)
  const [showMethods, setShowMethods] = useState(false)
  const { t } = useTranslation(['errors', 'global'])
  const { sm, screenHeight } = useResponsive()

  const total_amount = invoices.reduce((sum, inv) => sum + inv.total_amount, 0)
  const lateFees = invoices.reduce((sum, inv) => sum + (inv.lateFee || 0), 0)

  useEffect(() => {
    if (!paymentPlan && invoice && paymentMethod && modal) {
      setLoading(true)
      routes2.invoice
        .getPricingPackageFee({
          invoiceIds: invoices.map((i) => i._id),
          accountId: paymentMethod.id,
        })
        .then((r) => {
          if (r.pricingPackage) {
            const { totalAmount, discount } = r.pricingPackage
            const total =
              paymentMethod?.paymentMethodType === 'bank' && totalAmount
                ? (totalAmount || 0) - (discount || 0)
                : totalAmount
            setPricingPackage({
              ...r.pricingPackage,
              totalAmount: total,
            })
          }
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [invoice, invoices, modal, paymentMethod, paymentPlan, total_amount])

  const onPaymentMethodSelect = useCallback((method) => {
    setLoading(true)
    setShowMethods(false)
    setMethod(method)
  }, [])

  return (
    <>
      <BuilderBottomModal
        visible={modal}
        height={loading ? 600 : screenHeight < 720 ? '100%' : undefined}
        title={t('global:payInvoice.pay-invoice')}
        onClose={() => {
          setModal(false)
        }}
      >
        {loading ? (
          <View style={{ height: 500 }}>
            <Loading />
          </View>
        ) : (
          <>
            {account.newlyAdded && (
              <>
                <SuccessMessage account={account} />
                <Spacer height={36} />
              </>
            )}
            <DetailsRowItem
              leftText={`${t('global:payInvoice.invoice-amount')}:`}
              rightText={total_amount}
              money
            />
            <FeeOrDiscount
              paymentMethod={paymentMethod}
              pricingPackage={pricingPackage}
            />

            {!!lateFees && (
              <DetailsRowItem
                leftText={`${t('global:payInvoice.late-interest-charge')}:`}
                rightText={lateFees}
                money
              />
            )}

            <InputField
              label={t('global:payInvoice.total-amount')}
              editable={false}
              model={{
                totalAmount: currencyMask(
                  (pricingPackage?.totalAmount || total_amount) + lateFees,
                ),
              }}
              name={'totalAmount'}
              style={{ height: 56 }}
              inputOuterStyle={{ backgroundColor: '#F8F9F9' }}
            />

            <InputField
              label={t('global:payInvoice.To')}
              editable={false}
              model={
                builderCreatedInvoice
                  ? invoice.supplierInvitationDetails
                  : invoice.company
              }
              name="name"
              style={{ height: 56, color: 'black' }}
              inputOuterStyle={{ backgroundColor: '#F8F9F9' }}
            />
            <InputField
              label={t('global:payInvoice.payment-method')}
              model={{
                name: (paymentMethod.accountName || paymentMethod.name).split(
                  '/',
                )[0],
              }}
              name="name"
              style={{ height: 56, marginBottom: 20, color: 'red' }}
              onPress={() => {
                setShowMethods(true)
              }}
              showDropdown
            />
            <Spacer height={22} />
            <PayTermsAndConditions />
            {!sm ? (
              <Divider
                style={{
                  marginVertical: 20,
                  height: 1,
                  borderColor: '#F5F7F8',
                  width: '100%',
                }}
              />
            ) : (
              <Spacer />
            )}
            <PaymentButton
              invoices={invoices}
              account={paymentMethod}
              method={
                paymentMethod.paymentMethodType === 'bank' ? 'ach' : 'card'
              }
              builderCreatedInvoice={builderCreatedInvoice}
              navigation={navigation}
              onSuccess={() => {
                setModal(false)
              }}
            />
            <Spacer height={30} />
          </>
        )}
      </BuilderBottomModal>
      <ChoosePaymentModal
        visible={showMethods}
        onClose={(tab) => {
          tab !== 'Banks' && setModal(false)
          setShowMethods(false)
        }}
        onPress={onPaymentMethodSelect}
        showModal={showModal}
      />
    </>
  )
}
